// Libraries
import { Flex, Text } from '@chakra-ui/react';
// Components
import { DebouncedNumericInput } from '@molecules/index';
// Types
import { ColumnFilterProps } from '@organisms/index';
import { RangeFilterProps } from './RangeFilter.types';

/**
 * A component to display a range filter on the tables filters.
 *
 * @component
 * @template TableData - The type of data in the table.
 * @param {ColumnFilterProps<TableData, RangeFilterProps>} props - The component props.
 * @param {Column<TableData, RangeFilterProps>} props.column - The column filter props.
 * @param {RatingsButtonsFilterProps} props.props - The additional props specific to the range filter.
 * @returns {ReactElement} - The RangeFilter component.
 */
export const RangeFilter = <TableData,>({ column, props }: ColumnFilterProps<TableData, RangeFilterProps>) => {
  const { getFilterValue, setFilterValue, options } = column;
  const { name } = props;
  const valueOptions = options.map((option) => Number(option));
  const minValue = Math.min(...valueOptions);
  const maxValue = Math.max(...valueOptions);
  const initialValue = (getFilterValue() as [number, number]) ?? [minValue, maxValue];

  /**
   * Handles the change event for the minimum value in the range input.
   *
   * @function
   * @param val - The new value for the minimum.
   */
  const handleMinChange = (val: number) => {
    if (val === minValue && initialValue[1] === maxValue) {
      setFilterValue(undefined);
    }
    if (val !== minValue) {
      setFilterValue((oldValue: [number, number]) => [val, oldValue?.[1] ?? maxValue]);
    }
  };

  /**
   * Handles the change event for the maximum value in the range input.
   *
   * @function
   * @param val - The new value for the minimum.
   */
  const handleMaxChange = (val: number) => {
    if (val === maxValue && initialValue[0] === minValue) {
      setFilterValue(undefined);
    }
    if (val !== maxValue) {
      setFilterValue((oldValue: [number, number]) => [oldValue?.[0] ?? minValue, val]);
    }
  };

  return (
    <Flex direction='column' bg='table.footer' borderTop='section' p={4} gap={2}>
      <Text fontSize='m' fontWeight='medium'>
        {name}
      </Text>
      <Flex gap={4}>
        <DebouncedNumericInput
          label='Min'
          min={minValue}
          max={maxValue}
          step={500}
          defaultValue={minValue}
          value={initialValue[0] ?? minValue}
          onChange={handleMinChange}
        />
        <DebouncedNumericInput
          label='Max'
          min={minValue}
          max={maxValue}
          step={500}
          defaultValue={maxValue}
          value={initialValue[1] ?? maxValue}
          onChange={handleMaxChange}
        />
      </Flex>
    </Flex>
  );
};
